import {
  Registration,
  RegistrationFieldPermissionsField,
  Brand,
  PermissionReadWrite,
} from '@stax-core/types-omni';
import { useContext, useMemo } from 'react';
import { RegistrationStore, BrandStore } from '../context';

export function getFieldVisibilityLevel(
  registration: Registration | null,
  brand: Brand | null,
  field: RegistrationFieldPermissionsField,
): PermissionReadWrite | null {
  // special fields
  switch (field) {
    case 'chosen_processing_method':
      // if the product dropdown is not shown via brand
      // then hide the 'chosen_processing_method' field
      if (!brand?.showProductDropdown) {
        return null;
      }
      break;
    case 'annual_gross_ach_revenue': // falls through
    case 'avg_ach_transaction': // falls through
    case 'largest_ach_transaction':
      // if the ach volume fields are not shown via brand
      // then hide the above 3 fields
      if (!brand?.showAchVolumeFields) {
        return null;
      }
      break;
    default:
      break;
  }

  const visibilityLevel = registration?.field_permissions?.[field];
  // if this field isn't present in fieldPermissions,
  // then default to 'write'
  return visibilityLevel === undefined ? 'write' : visibilityLevel;
}

export function getCombinedFieldsVisibilityLevels(
  registration: Registration | null,
  brand: Brand | null,
  ...fields: RegistrationFieldPermissionsField[]
) {
  let atLeastOneWrite = false;
  let atLeastOneRead = false;
  let atLeastOneNone = false;

  fields.forEach(f => {
    const visibilityLevel = getFieldVisibilityLevel(registration, brand, f);

    switch (visibilityLevel) {
      case 'write':
        atLeastOneWrite = true;
        break;
      case 'read':
        atLeastOneRead = true;
        break;
      case null:
        atLeastOneNone = true;
        break;
    }
  });

  // if there's at least one writeable, but none read-only and none "none"
  // that means all are writeable
  const allWrite = atLeastOneWrite && !atLeastOneRead && !atLeastOneNone;
  // if there's at least one read-only or at least one writeable, but no "none"
  // that means all are readable
  const allRead = (atLeastOneWrite || atLeastOneRead) && !atLeastOneNone;
  // if there's at least one "none", but none read-only and none writeable
  // that means all are "none"
  const allNone = !atLeastOneWrite && !atLeastOneRead && atLeastOneNone;

  if (atLeastOneWrite) {
    // after calculating the all* consts, if there was at least one write
    // then that means we can also read
    atLeastOneRead = true;
  }

  return {
    allWrite,
    allRead,
    allNone,

    atLeastOneWrite,
    atLeastOneRead,
    atLeastOneNone,
    // TypeScript thinks these are always 'false', cast to boolean until we enable TS null checking
    write: allWrite as boolean,
    read: allRead as boolean,
    none: allNone as boolean,
  };
}

export function useFieldPermissionFlag(
  ...fields: RegistrationFieldPermissionsField[]
) {
  const {
    state: { brand },
  } = useContext(BrandStore);

  const {
    state: { registration },
  } = useContext(RegistrationStore);

  // we use joinedFields as a dependency for useEffect
  // to prevent the useEffect from firing every render
  // if 'fields' is not memoized
  const joinedFields = fields.join('|');

  const fieldsVisibilityLevels = useMemo(() => {
    const fields = joinedFields.split(
      '|',
    ) as RegistrationFieldPermissionsField[];
    return getCombinedFieldsVisibilityLevels(registration, brand, ...fields);
  }, [joinedFields, registration, brand]);

  return fieldsVisibilityLevels;
}
